import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { isEmpty } from 'lodash'
import { IconPencil } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { renameTask } from '@/reducers/tasks/tasks.actions'
import { COACHING_TASK_TYPE, DEFAULT_COACHING_TITLE } from '@/reducers/tasks/tasks.constants'

export const TaskName = ({ task }) => {
  const dispatch = useDispatch()
  // Tracks *which* task is being edited rather than a bare boolean. The drawer
  // stays mounted across task switches (Drawer passes id as a prop, not a key),
  // so a boolean would leave a half-typed title in place and rename whichever
  // task landed in the drawer next.
  const [editingTaskId, setEditingTaskId] = useState(null)
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)

  const isEditing = editingTaskId === task.id

  // Only coaching titles are free text. The compliance inbox filters on
  // task.name as a category (inbox.helpers.js), so renaming a compliance task
  // would drop it out of its own filter.
  const canRename = task.type === COACHING_TASK_TYPE

  const startEditing = () => {
    setName(task.name || '')
    setEditingTaskId(task.id)
  }

  const stopEditing = () => {
    setEditingTaskId(null)
  }

  const handleSave = async () => {
    // The Save button disables itself while loading, but Enter does not.
    if (saving) {
      return
    }

    const trimmed = name.trim()

    if (isEmpty(trimmed) || trimmed === task.name) {
      stopEditing()
      return
    }

    setSaving(true)
    const response = await dispatch(renameTask(task.id, trimmed, task.organization_id))
    setSaving(false)

    // Stay open on failure so the typed title isn't lost to a retry.
    if (response) {
      stopEditing()
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSave()
    }

    if (event.key === 'Escape') {
      stopEditing()
    }
  }

  if (!canRename) {
    return <h2>{task.name}</h2>
  }

  if (isEditing) {
    return (
      <div className="task-name editing">
        <input
          autoFocus
          className="task-name__input"
          aria-label="Coaching title"
          data-testid="task-name-input"
          value={name}
          maxLength={255}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button brand small loading={saving} data-testid="task-name-save" onClick={handleSave}>
          Save
        </Button>
        <Button small disabled={saving} data-testid="task-name-cancel" onClick={stopEditing}>
          Cancel
        </Button>
      </div>
    )
  }

  return (
    <div className="task-name">
      <h2>{task.name || DEFAULT_COACHING_TITLE}</h2>
      <Button
        text
        tooltip="Rename"
        aria-label="Rename coaching title"
        data-testid="task-name-edit"
        icon={<IconPencil />}
        onClick={startEditing}
      />
    </div>
  )
}
